import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, ItemsEditor, CustomerCombobox, QuickCustomerDialog, QuickVehicleDialog } from "@/components/common";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, Sparkles, Boxes } from "lucide-react";
import { toast } from "sonner";
import { WarehousePicker } from "@/components/WarehousePicker";
import { ConsumableSuggest, consumableSettings, partsBase, hasConsumable } from "@/components/ConsumableSuggest";

const VAT_CHOICES = [["SK", "SK – 23% DPH"], ["RC", "0% – do zahraničia"]];
const empty = { title: "", status: "rozpracovana", vat_mode: "SK", vehicle_id: "", customer_id: "", odometer_km: "", oil_change: false, items: [], employees: [], note: "" };
const _norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const detectInspections = (items) => {
  const n = _norm((items || []).map((it) => it.description || "").join(" | "));
  return {
    tk: /\btk\b/.test(n) || /\bstk\b/.test(n) || /technick\w*\s+kontrol/.test(n),
    ek: /\bek\b/.test(n) || /emisn\w*\s+kontrol/.test(n),
    oil: /olej/.test(n),
  };
};

export default function JobForm() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [settings, setSettings] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiCost, setAiCost] = useState(null);
  const [aiUnlimited, setAiUnlimited] = useState(false);
  const [inspect, setInspect] = useState({ open: false, tk: false, ek: false, oil: false });
  const [whOpen, setWhOpen] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [suggest, setSuggest] = useState(null);
  const [consumableIds, setConsumableIds] = useState(new Set());
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    api.get("/employees").then((r) => setAllEmployees(r.data || [])).catch(() => {});
    api.get("/warehouse").then((r) => setConsumableIds(new Set((r.data || []).filter((it) => it.consumable).map((it) => it.id)))).catch(() => {});
    api.get("/billing/me").then((r) => { setAiCost(r.data?.costs?.ai ?? null); setAiUnlimited(!!r.data?.unlimited); }).catch(() => {});
  }, []);
  useEffect(() => {
    const pv = location.state?.prefillVehicle;
    if (!id && pv) setForm((f) => ({ ...f, vehicle_id: pv.id, customer_id: pv.customer_id || "" }));
  }, [id, location.state]);
  useEffect(() => {
    if (!id) return;
    api.get("/jobs").then((r) => {
      const j = r.data.find((x) => x.id === id);
      if (j) setForm({ ...empty, ...j, oil_change: false, odometer_km: j.odometer_km ?? "", vehicle_id: j.vehicle_id || "", customer_id: j.customer_id || "", vat_mode: j.vat_mode || "SK" });
      setLoading(false);
    });
  }, [id]);

  const filteredVehicles = form.customer_id ? vehicles.filter((v) => v.customer_id === form.customer_id) : vehicles;
  const onCustomerChange = (cid) => {
    setForm((f) => {
      const keep = f.vehicle_id && vehicles.find((x) => x.id === f.vehicle_id && (!cid || x.customer_id === cid));
      return { ...f, customer_id: cid, vehicle_id: keep ? f.vehicle_id : "" };
    });
  };
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCustName, setNewCustName] = useState("");
  const setVatMode = (mode) => setForm((f) => ({ ...f, vat_mode: mode, items: f.items.map((it) => ({ ...it, vat_rate: mode === "RC" ? 0 : 23 })) }));

  const [qaOpen, setQaOpen] = useState(false);
  const openQuickAdd = () => { if (!form.customer_id) { toast.error("Najprv vyberte zákazníka"); return; } setQaOpen(true); };

  const computeNormo = async () => {
    if (!form.items.some((it) => it.description?.trim()) && !form.title.trim()) {
      toast.error("Zadajte názov zákazky alebo aspoň jednu položku"); return;
    }
    setAiLoading(true); setAi(null);
    try {
      const veh = vehicles.find((x) => x.id === form.vehicle_id) || {};
      const { data } = await api.post("/ai/normohodiny", { vehicle: veh, items: form.items, title: form.title });
      setAi(data);
      if (data.credit_charged) toast.info(`−${data.credit_charged} ${data.credit_charged === 1 ? "kredit" : "kreditov"} · zostáva ${data.credits_left}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };
  const insertNormo = () => {
    const rate = Number(settings.labor_rate || 0);
    const vr = form.vat_mode === "RC" ? 0 : 23;
    const rows = (ai?.operations || []).map((o) => ({ description: o.name, kind: "praca", qty: o.hours, unit: "h", unit_price: rate, discount: 0, vat_rate: vr }));
    if (!rows.length) return;
    set("items", [...form.items, ...rows]);
    rows.forEach((r) => api.post("/work-catalog", { name: r.description, hours: r.qty, unit_price: r.unit_price }).catch(() => {}));
    setAi(null);
    toast.success(`Pridané ${rows.length} položiek práce`);
  };

  const addFromStock = (rows) => {
    if (!rows?.length) return;
    set("items", [...form.items, ...rows]);
    toast.success(`Zo skladu pridané: ${rows.length} ${rows.length === 1 ? "položka" : "položiek"}`);
  };

  const addEmp = (empId) => {
    if (!empId || (form.employees || []).some((e) => e.employee_id === empId)) return;
    set("employees", [...(form.employees || []), { employee_id: empId, minutes: 0 }]);
  };
  const removeEmp = (empId) => set("employees", (form.employees || []).filter((e) => e.employee_id !== empId));
  const setEmpMinutes = (empId, h, m) => set("employees", (form.employees || []).map((e) => e.employee_id === empId ? { ...e, minutes: Math.max(0, (Number(h) || 0) * 60 + (Number(m) || 0)) } : e));
  const normoHours = (form.items || []).filter((it) => it.kind === "praca").reduce((s, it) => s + Number(it.qty || 0), 0);
  const workedMinutes = (form.employees || []).reduce((s, e) => s + Number(e.minutes || 0), 0);
  const empName = (eid) => (allEmployees.find((x) => x.id === eid) || {}).name || "—";

  const save = async () => {
    if (!form.title.trim()) { toast.error("Zadajte názov zákazky"); return; }
    const cfg = consumableSettings(settings);
    const base = partsBase(form.items, consumableIds);
    if (cfg.on && base > 0 && !hasConsumable(form.items, consumableIds)) { setSuggest(base); return; }
    await doSave(form.items);
  };
  const doSave = async (items) => {
    setSuggest(null);
    setSaving(true);
    const payload = { ...form, odometer_km: form.odometer_km !== "" ? Number(form.odometer_km) : null, vehicle_id: form.vehicle_id || null, customer_id: form.customer_id || null,
      items: items.map((it) => ({ ...it, qty: Number(it.qty), unit_price: Number(it.unit_price || 0), discount: Number(it.discount || 0), vat_rate: form.vat_mode === "RC" ? 0 : Number(it.vat_rate || 0) })) };
    try {
      if (id) await api.put(`/jobs/${id}`, payload); else await api.post("/jobs", payload);
      toast.success("Zákazka uložená");
      const det = detectInspections(items);
      const pending = { tk: det.tk, ek: det.ek, oil: settings.suggest_oil !== false && det.oil && !form.oil_change };
      if (form.vehicle_id && (pending.tk || pending.ek || pending.oil)) {
        setInspect({ open: true, ...pending });
      } else {
        navigate("/zakazky");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmInspect = async () => {
    try {
      await api.post(`/vehicles/${form.vehicle_id}/record-inspection`, { tk: inspect.tk, ek: inspect.ek, oil: inspect.oil });
      toast.success("Zaznamenané k vozidlu");
    } catch { toast.error("Nepodarilo sa zaznamenať k vozidlu"); }
    setInspect({ open: false, tk: false, ek: false, oil: false });
    navigate("/zakazky");
  };
  const skipInspect = () => { setInspect({ open: false, tk: false, ek: false, oil: false }); navigate("/zakazky"); };

  if (loading) return <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div>
      <Button variant="ghost" className="mb-2 gap-2" onClick={() => navigate("/zakazky")} data-testid="job-form-back"><ArrowLeft className="h-4 w-4" /> Späť</Button>
      <PageHeader title={id ? "Upraviť zákazku" : "Nová zákazka"} subtitle="Vyplňte údaje zákazky" />
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="card-surface p-4 space-y-4">
          <div className="space-y-1.5"><Label>Názov zákazky</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Napr. Výmena bŕzd" data-testid="job-title-input" /></div>
          <div>
            <Label className="mb-2 block">Sadzba DPH pre položky</Label>
            <div className="flex gap-1 rounded-lg border border-border bg-background/60 p-1">
              {VAT_CHOICES.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setVatMode(k)} data-testid={`job-vat-${k}`} className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${form.vat_mode === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>Zákazník</Label><button type="button" onClick={() => { setNewCustName(""); setNewCustOpen(true); }} className="text-xs font-semibold text-primary hover:underline" data-testid="job-quick-add-customer">+ Nový zákazník</button></div>
              <CustomerCombobox customers={customers} value={form.customer_id} onChange={onCustomerChange}
                onAddNew={(name) => { setNewCustName(name); setNewCustOpen(true); }} placeholder="Vyberte zákazníka" testid="job-customer" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>Vozidlo</Label><button type="button" onClick={openQuickAdd} className="text-xs font-semibold text-primary hover:underline" data-testid="job-quick-add-vehicle">+ Nové vozidlo</button></div>
              <Select value={form.vehicle_id || "none"} onValueChange={(v) => set("vehicle_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="job-vehicle-select"><SelectValue placeholder={form.customer_id ? "Vyberte vozidlo" : "Najprv vyberte zákazníka"} /></SelectTrigger>
                <SelectContent><SelectItem value="none">Bez vozidla</SelectItem>{filteredVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.spz} · {v.brand} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Stav tachometra (KM)</Label><Input type="number" value={form.odometer_km} onChange={(e) => set("odometer_km", e.target.value)} data-testid="job-km-input" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex min-h-[48px] cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-background/40 px-3 py-2.5" data-testid="job-oil-change-label">
              <Checkbox checked={!!form.oil_change} onCheckedChange={(v) => set("oil_change", !!v)} data-testid="job-oil-change" />
              <span className="text-sm font-medium">Výmena oleja</span>
            </label>
          </div>
        </div>
        <div className="card-surface p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Label className="text-base font-bold">Položky (práca + materiál)</Label>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setWhOpen(true)} data-testid="job-show-warehouse-button">
                <Boxes className="h-4 w-4 text-primary" /> Zobraziť sklad
              </Button>
              <Button type="button" variant="outline" size="sm" className="gap-2" onClick={computeNormo} disabled={aiLoading} data-testid="ai-normo-button">
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-primary" />} Vypočítať normohodiny (AI){!aiUnlimited && aiCost != null ? ` · ${aiCost} ${aiCost === 1 ? "kredit" : "kreditov"}` : ""}
              </Button>
            </div>
          </div>
          <ItemsEditor items={form.items} onChange={(v) => set("items", v)} vatMode={form.vat_mode} />
          {ai && (
            <div className="mt-4 rounded-lg border border-primary/40 bg-primary/5 p-4" data-testid="ai-normo-result">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase"><Sparkles className="h-4 w-4 text-primary" /> Návrh normohodín</div>
              <div className="space-y-1.5">
                {(ai.operations || []).map((o, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 border-b border-border/40 pb-1.5 text-sm" data-testid={`ai-normo-op-${i}`}>
                    <span className="min-w-0"><span className="font-medium">{o.name}</span>{o.note && <span className="block text-xs text-muted-foreground">{o.note}</span>}</span>
                    <span className="shrink-0 font-mono font-semibold">{o.hours} h</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between text-sm font-bold"><span>Spolu</span><span className="font-mono">{ai.total_hours} h</span></div>
              <p className="mt-2 text-xs text-muted-foreground">{ai.disclaimer}{!settings.labor_rate ? " · Tip: nastavte sadzbu €/h v Nastaveniach pre automatický prepočet ceny." : ` · Sadzba ${settings.labor_rate} €/h`}</p>
              <div className="mt-3 flex gap-2">
                <Button type="button" size="sm" onClick={insertNormo} data-testid="ai-normo-insert">Vložiť do položiek</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setAi(null)}>Zrušiť</Button>
              </div>
            </div>
          )}
        </div>
        <div className="card-surface p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-base font-bold">Zamestnanci na zákazke</Label>
            <Select value="" onValueChange={addEmp}>
              <SelectTrigger className="w-56" data-testid="job-add-employee"><SelectValue placeholder="Priradiť zamestnanca" /></SelectTrigger>
              <SelectContent>{allEmployees.filter((e) => e.active && !(form.employees || []).some((x) => x.employee_id === e.id)).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.position ? ` · ${e.position}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {(form.employees || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Zatiaľ nikto nie je priradený. Priraďte zamestnanca a zapíšte odpracovaný čas.</p>
          ) : (
            <div className="space-y-2">
              {(form.employees || []).map((e) => {
                const h = Math.floor(Number(e.minutes || 0) / 60), m = Number(e.minutes || 0) % 60;
                return (
                  <div key={e.employee_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background/40 px-3 py-2" data-testid={`job-emp-row-${e.employee_id}`}>
                    <span className="min-w-[140px] flex-1 font-medium">{empName(e.employee_id)}</span>
                    <div className="flex items-center gap-1">
                      <Input type="number" min="0" className="h-9 w-16 text-right" value={h} onChange={(ev) => setEmpMinutes(e.employee_id, ev.target.value, m)} data-testid={`job-emp-hours-${e.employee_id}`} /><span className="text-sm text-muted-foreground">h</span>
                      <Input type="number" min="0" max="59" className="h-9 w-16 text-right" value={m} onChange={(ev) => setEmpMinutes(e.employee_id, h, ev.target.value)} data-testid={`job-emp-mins-${e.employee_id}`} /><span className="text-sm text-muted-foreground">min</span>
                    </div>
                    <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => removeEmp(e.employee_id)} data-testid={`job-emp-remove-${e.employee_id}`}>Odobrať</Button>
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2 text-sm" data-testid="job-emp-summary">
                <span>Odpracované spolu: <b className="font-mono">{Math.floor(workedMinutes / 60)}h {workedMinutes % 60}min</b></span>
                <span>Normohodiny (práca): <b className="font-mono">{normoHours} h</b>{workedMinutes > 0 && normoHours > 0 && <span className="ml-2 text-muted-foreground">· efektívnosť <b className={`${(normoHours / (workedMinutes / 60)) * 100 >= 100 ? "text-emerald-400" : "text-amber-400"}`}>{Math.round((normoHours / (workedMinutes / 60)) * 100)}%</b></span>}</span>
              </div>
            </div>
          )}
        </div>

        <div className="card-surface p-5 space-y-1.5"><Label>Poznámka</Label><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="job-note-input" /></div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => navigate("/zakazky")}>Zrušiť</Button>
          <Button onClick={save} disabled={saving} data-testid="save-job-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť zákazku"}</Button>
        </div>
      </div>

      <WarehousePicker open={whOpen} onOpenChange={setWhOpen} vatMode={form.vat_mode}
        defaultMargin={settings.default_margin} warehouseVatRate={settings.warehouse_vat_rate} onAdd={addFromStock} />

      <QuickVehicleDialog open={qaOpen} onOpenChange={setQaOpen} customerId={form.customer_id}
        onCreated={(v) => { setVehicles((prev) => [...prev, v]); set("vehicle_id", v.id); }} />

      <QuickCustomerDialog open={newCustOpen} onOpenChange={setNewCustOpen} initialName={newCustName}
        onCreated={(c) => { setCustomers((prev) => [...prev, c]); onCustomerChange(c.id); }}
        onPickExisting={(c) => onCustomerChange(c.id)} />
      <ConsumableSuggest open={suggest != null} base={suggest || 0} settings={settings} vatMode={form.vat_mode}
        onAdd={(rows) => { const items = [...form.items, ...rows]; set("items", items); doSave(items); }}
        onSkip={() => doSave(form.items)} onBack={() => setSuggest(null)} />

      <Dialog open={inspect.open} onOpenChange={(o) => { if (!o) skipInspect(); }}>
        <DialogContent className="sm:max-w-md" data-testid="inspection-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase">Zaznamenať k vozidlu?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">V zákazke sme rozpoznali tieto úkony. Zaškrtnuté priradíme k vozidlu a nastavíme pripomienku.</p>
            <div className="space-y-2">
              {inspect.tk && (
                <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-background/40 px-3 py-2.5" data-testid="inspect-tk-label">
                  <Checkbox checked={inspect.tk} onCheckedChange={(v) => setInspect((s) => ({ ...s, tk: !!v }))} data-testid="inspect-tk" />
                  <span className="text-sm font-medium">Technická kontrola (TK)</span>
                </label>
              )}
              {inspect.ek && (
                <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-background/40 px-3 py-2.5" data-testid="inspect-ek-label">
                  <Checkbox checked={inspect.ek} onCheckedChange={(v) => setInspect((s) => ({ ...s, ek: !!v }))} data-testid="inspect-ek" />
                  <span className="text-sm font-medium">Emisná kontrola (EK)</span>
                </label>
              )}
              {inspect.oil && (
                <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-background/40 px-3 py-2.5" data-testid="inspect-oil-label">
                  <Checkbox checked={inspect.oil} onCheckedChange={(v) => setInspect((s) => ({ ...s, oil: !!v }))} data-testid="inspect-oil" />
                  <span className="text-sm font-medium">Výmena oleja</span>
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={skipInspect} data-testid="inspect-skip">Preskočiť</Button>
            <Button onClick={confirmInspect} disabled={!inspect.tk && !inspect.ek && !inspect.oil} data-testid="inspect-confirm">Priradiť</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
